import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Popover,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  SettingsOutlined as SettingsIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from '@/constants/route-config';
import { getLocalizedPath } from '@/utils/locale';
import { useTranslation } from 'react-i18next';
import type { UserProfile } from '@/types/api/response/user';
import type { ColorMode } from '@/theme/tokens';
import { useCreditBalance } from '@/api/axios/credit';

export type ProfilePopoverPlacement = 'header' | 'sidebar';

interface UserProfilePopoverProps {
  anchorEl: HTMLElement | null;
  placement: ProfilePopoverPlacement;
  profile?: UserProfile;
  profileLoading: boolean;
  profileError?: unknown;
  fallbackUser?: {
    name?: string | null;
    nickname?: string | null;
    email?: string | null;
    picture?: string | null;
  };
  mode: ColorMode;
  onClose: () => void;
  onToggleTheme: () => void;
  onLogout: () => void;
}

const formatBalance = (value: number | undefined) =>
  value === undefined ? '—' : value.toLocaleString();

const BalanceCard: React.FC<{
  label: string;
  value?: number;
  loading: boolean;
}> = ({ label, value, loading }) => {
  const { tokens } = useTheme();

  return (
    <Box
      sx={{
        minWidth: 0,
        p: '12px 14px',
        borderRadius: 1,
        border: `1px solid ${tokens.border}`,
        bgcolor: tokens.surface,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.5px',
          color: tokens.textSecondary,
          textTransform: 'uppercase',
          overflowWrap: 'anywhere',
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={54} height={24} />
      ) : (
        <Typography
          sx={{
            mt: '6px',
            fontFamily: tokens.monoFontFamily,
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.35,
            fontVariantNumeric: 'tabular-nums',
            overflowWrap: 'anywhere',
            color: tokens.textPrimary,
          }}
        >
          {formatBalance(value)}
        </Typography>
      )}
    </Box>
  );
};

/** Shared profile popover opened by either authenticated app-shell avatar. */
export const UserProfilePopover: React.FC<UserProfilePopoverProps> = ({
  anchorEl,
  placement,
  profile,
  profileLoading,
  profileError,
  fallbackUser,
  mode,
  onClose,
  onToggleTheme,
  onLogout,
}) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const balance = useCreditBalance({ enabled: Boolean(anchorEl) });
  const displayName =
    profile?.name ||
    profile?.username ||
    fallbackUser?.name ||
    fallbackUser?.nickname ||
    fallbackUser?.email ||
    t('layout.user', { defaultValue: 'User' });
  const email = profile?.email || fallbackUser?.email || '';
  const image = profile?.image || fallbackUser?.picture || undefined;
  const initial = displayName.charAt(0).toUpperCase();
  const isSidebar = placement === 'sidebar';

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={
        isSidebar
          ? { vertical: 'bottom', horizontal: 'left' }
          : { vertical: 'top', horizontal: 'right' }
      }
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': t('layout.profile-menu.label', {
            defaultValue: 'User profile',
          }),
          sx: {
            width: 340,
            maxWidth: 'calc(100vw - 40px)',
            boxSizing: 'border-box',
            ml: isSidebar ? 1 : 0,
            mt: isSidebar ? 0 : 0.75,
            overflowX: 'hidden',
            overflowY: 'auto',
            border: `1px solid ${tokens.border}`,
            borderRadius: 1.5,
            bgcolor: tokens.surface,
            backgroundImage: 'none',
            boxShadow: '0 18px 46px rgba(2,10,22,0.4)',
          },
        },
      }}
    >
      <Box
        sx={{
          p: '16px 16px 14px',
          background: 'linear-gradient(135deg,#051628 0%,#0A2540 100%)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            src={image}
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              bgcolor: tokens.accent,
              color: tokens.onAccent,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {initial}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {profileLoading && !profile ? (
              <>
                <Skeleton
                  width="70%"
                  sx={{ bgcolor: 'rgba(255,255,255,0.16)' }}
                />
                <Skeleton
                  width="90%"
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                />
              </>
            ) : (
              <>
                <Typography
                  noWrap
                  title={displayName}
                  sx={{ fontSize: 14.5, fontWeight: 700, color: '#fff' }}
                >
                  {displayName}
                </Typography>
                <Typography
                  noWrap
                  title={email}
                  sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)' }}
                >
                  {email}
                </Typography>
              </>
            )}
          </Box>
        </Stack>
      </Box>

      <Stack
        spacing={1.25}
        sx={{ p: '14px 16px', bgcolor: tokens.surface2 }}
      >
        <BalanceCard
          label={t('layout.profile-menu.subscription', {
            defaultValue: 'Subscription',
          })}
          value={balance.data?.subscription}
          loading={balance.isLoading}
        />
        <BalanceCard
          label={t('layout.profile-menu.notification', {
            defaultValue: 'Notification',
          })}
          value={balance.data?.notification}
          loading={balance.isLoading}
        />
      </Stack>

      {(profileError && !profile && !profileLoading) || balance.isError ? (
        <Typography
          role="status"
          sx={{
            px: 1.75,
            py: 1,
            fontSize: 11.5,
            color: tokens.status.failed.fg,
          }}
        >
          {t('layout.profile-menu.load-failed', {
            defaultValue: 'Profile details are temporarily unavailable.',
          })}
        </Typography>
      ) : null}

      <Box sx={{ px: 1.75, pb: 1.5, bgcolor: tokens.surface2 }}>
        <Button
          component={RouterLink}
          to={getLocalizedPath(ROUTES.SETTINGS)}
          onClick={onClose}
          fullWidth
          color="inherit"
          startIcon={<SettingsIcon sx={{ fontSize: 18 }} />}
          endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
          sx={{
            justifyContent: 'flex-start',
            px: 1.5,
            py: 1,
            fontSize: 13,
            color: tokens.textSecondary,
            '& .MuiButton-endIcon': { ml: 'auto' },
            '&:hover': { bgcolor: tokens.surface, color: tokens.textPrimary },
          }}
        >
          {t('settings.title', { defaultValue: 'Settings' })}
        </Button>
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 1.75,
          py: 1,
          gap: 1,
          flexWrap: 'wrap',
          borderTop: `1px solid ${tokens.border}`,
          bgcolor: tokens.surface2,
        }}
      >
        <Button
          color="inherit"
          size="small"
          onClick={onToggleTheme}
          startIcon={
            mode === 'dark' ? (
              <LightModeIcon sx={{ fontSize: 14 }} />
            ) : (
              <DarkModeIcon sx={{ fontSize: 14 }} />
            )
          }
          sx={{
            minWidth: 0,
            px: 0,
            color: tokens.textSecondary,
            fontSize: 12,
            fontWeight: 500,
            textTransform: 'none',
          }}
        >
          {mode === 'dark'
            ? t('layout.profile-menu.light-mode', {
                defaultValue: 'Light mode',
              })
            : t('layout.profile-menu.dark-mode', { defaultValue: 'Dark mode' })}
        </Button>
        <Button
          color="inherit"
          size="small"
          onClick={onLogout}
          startIcon={<LogoutIcon sx={{ fontSize: 14 }} />}
          sx={{
            minWidth: 0,
            px: 0,
            color: tokens.status.failed.fg,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {t('layout.logout', { defaultValue: 'Logout' })}
        </Button>
      </Stack>
    </Popover>
  );
};
